import type { CalendarView, ConnectionView, EventView, ReceivedShareView } from "@confluence/shared";
import { keepPreviousData, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { get } from "../api";
import { useConnections } from "../pages/calendars";
import { addDays, zonedInstant, type DayKey } from "./time";

/** Plage interrogée pour les jours [from, to[ : un jour de marge de chaque côté pour les journées
 *  entières, stockées en UTC, et les fuseaux éloignés. */
function bounds(from: DayKey, to: DayKey, tz: string) {
  return [zonedInstant(addDays(from, -1), 0, tz).toISOString(), zonedInstant(addDays(to, 1), 0, tz).toISOString()] as const;
}

let showBlocks = false;
/** Préférence d'affichage : blocs du blocage croisé montrés discrètement plutôt que masqués. */
export const setShowBlocks = (v: boolean | undefined) => {
  showBlocks = !!v;
};

/** Requêtes d'une plage : mes agendas, puis chaque partage affiché. */
function rangeQueries(from: DayKey, to: DayKey, tz: string, shareIds: string[]) {
  const [start, end] = bounds(from, to, tz);
  return [
    {
      queryKey: ["events", start, end, showBlocks],
      queryFn: async () => (await get<{ events: EventView[] }>(`/api/events?from=${start}&to=${end}${showBlocks ? "&blocks=true" : ""}`)).events,
      staleTime: 30_000,
    },
    ...shareIds.map((id) => ({
      queryKey: ["events", "share", id, start, end],
      queryFn: async () => (await get<{ events: EventView[] }>(`/api/shares/${id}/events?from=${start}&to=${end}`)).events,
      staleTime: 30_000,
    })),
  ];
}

const merge = (lists: (EventView[] | undefined)[]) => {
  const seen = new Map<string, EventView>();
  for (const list of lists) for (const ev of list ?? []) seen.set(ev.id, ev);
  return [...seen.values()];
};

const LIVE = { refetchInterval: 5 * 60_000, refetchOnWindowFocus: true } as const;

/** Événements des jours [from, to[ (les miens et ceux qu'on me partage), en gardant l'affichage
 *  précédent pendant le chargement. */
export function useEvents(from: DayKey, to: DayKey, tz: string, shareIds: string[], prefetch?: { prev: [DayKey, DayKey]; next: [DayKey, DayKey] }) {
  const qc = useQueryClient();
  const results = useQueries({ queries: rangeQueries(from, to, tz, shareIds).map((q) => ({ ...q, ...LIVE, placeholderData: keepPreviousData })) });
  const pending = results.some((r) => r.isPending);
  useEffect(() => {
    if (!prefetch || pending) return;
    for (const range of [prefetch.next, prefetch.prev]) for (const q of rangeQueries(...range, tz, shareIds)) void qc.prefetchQuery(q);
  }, [from, to, tz, pending, shareIds.join()]);
  return { data: merge(results.map((r) => r.data)), isFetching: results.some((r) => r.isFetching) };
}

/** Plusieurs plages à la fois (vue agenda continue). */
export function useEventChunks(chunks: [DayKey, DayKey][], tz: string, shareIds: string[]) {
  const results = useQueries({ queries: chunks.flatMap(([from, to]) => rangeQueries(from, to, tz, shareIds)).map((q) => ({ ...q, ...LIVE })) });
  return { data: merge(results.map((r) => r.data)), isPending: results.some((r) => r.isPending) };
}

export function useShares() {
  return useQuery({
    queryKey: ["shares"],
    queryFn: () => get<{ mine: import("@confluence/shared").OwnedShareView[]; withMe: ReceivedShareView[] }>("/api/shares"),
    staleTime: 60_000,
  });
}

export interface CalendarInfo extends CalendarView {
  connection: ConnectionView;
  /** Présent pour un agenda partagé par quelqu'un d'autre. */
  share?: ReceivedShareView;
}

/** Agendas indexés par identifiant, avec leur compte ; les partages reçus y figurent comme des
 *  agendas en lecture seule (identifiant « share:… »). */
export function useCalendarIndex() {
  const connections = useConnections();
  const shares = useShares();
  const index = useMemo(() => {
    const map = new Map<string, CalendarInfo>();
    for (const c of connections.data ?? []) for (const cal of c.calendars) map.set(cal.id, { ...cal, connection: c });
    for (const s of shares.data?.withMe ?? []) {
      const id = `share:${s.id}`;
      map.set(id, {
        id,
        connectionId: id,
        name: s.label || s.owner.name,
        remoteName: s.owner.name,
        color: s.color,
        shown: !s.hidden,
        countsAsBusy: false,
        writable: false,
        defaultVisibility: "busy",
        isDefault: false,
        share: s,
        connection: { id, provider: "ics", label: s.owner.email, status: "ok", lastError: null, lastSyncedAt: null, syncing: false, calendars: [] },
      });
    }
    return map;
  }, [connections.data, shares.data]);
  const shareIds = useMemo(() => (shares.data?.withMe ?? []).filter((s) => !s.hidden).map((s) => s.id), [shares.data]);
  return { connections, shares, index, shareIds };
}

/** Mises à jour en direct : le serveur prévient dès qu'un agenda change (server-sent events). */
export function useLiveUpdates() {
  const qc = useQueryClient();
  useEffect(() => {
    if (typeof EventSource === "undefined") return;
    const es = new EventSource("/api/stream");
    es.addEventListener("events", () => void qc.invalidateQueries({ queryKey: ["events"] }));
    es.addEventListener("connections", () => void qc.invalidateQueries({ queryKey: ["connections"] }));
    es.addEventListener("shares", () => void qc.invalidateQueries({ queryKey: ["shares"] }));
    // Après une coupure (veille, réseau), on recharge ce qui a pu changer entre-temps.
    let wasDown = false;
    es.onerror = () => {
      wasDown = true;
    };
    es.onopen = () => {
      if (wasDown) void qc.invalidateQueries({ queryKey: ["events"] });
      wasDown = false;
    };
    return () => es.close();
  }, [qc]);
}
